import { createServerFn } from '@tanstack/react-start';
import { createEmptyCard, Rating, type Grade } from 'ts-fsrs';
import { CURRENT_MUSHAF } from '@/shared/constants/constants';
import { toCardRow, toFsrsCard, type CardRow } from './card-mapper';
import { rateCard as scheduleCard } from './scheduling';

const MAX_PAGE = 604;

function validatePageNumber(n: unknown): number {
  if (typeof n !== 'number' || !Number.isInteger(n) || n < 1 || n > MAX_PAGE) {
    throw new Error(`Invalid page number: ${String(n)}`);
  }
  return n;
}

function validateGrade(g: unknown): Grade {
  if (
    g !== Rating.Again &&
    g !== Rating.Hard &&
    g !== Rating.Good &&
    g !== Rating.Easy
  ) {
    throw new Error(`Invalid rating: ${String(g)}`);
  }
  return g as Grade;
}

interface CardTable {
  card: CardRow;
}

async function serverDeps() {
  const { env } = await import('cloudflare:workers');
  const { getRequestHeaders } = await import('@tanstack/react-start/server');
  const { Kysely } = await import('kysely');
  const { D1Dialect } = await import('kysely-d1');
  const { createAuth } = await import('@/features/auth/lib/auth.server');

  const db = new Kysely<CardTable>({
    dialect: new D1Dialect({ database: env.DB }),
  });

  const auth = createAuth(env.DB);
  const headers = getRequestHeaders();
  const session = await auth.api.getSession({
    headers: headers as unknown as Headers,
  });
  if (!session?.user?.id) {
    throw new Error('Unauthorized');
  }

  return { db, userId: session.user.id };
}

export const getCard = createServerFn({ method: 'GET' })
  .inputValidator((data: { pageNumber: number }) => ({
    pageNumber: validatePageNumber(data.pageNumber),
  }))
  .handler(async ({ data }) => {
    const { db, userId } = await serverDeps();

    const row = await db
      .selectFrom('card')
      .selectAll()
      .where('user_id', '=', userId)
      .where('page_number', '=', data.pageNumber)
      .where('mushaf', '=', CURRENT_MUSHAF)
      .executeTakeFirst();

    if (!row) return null;

    return {
      id: row.id,
      pageNumber: row.page_number,
      card: toFsrsCard(row),
    };
  });

export const rateCard = createServerFn({ method: 'POST' })
  .inputValidator((data: { pageNumber: number; rating: Grade }) => ({
    pageNumber: validatePageNumber(data.pageNumber),
    rating: validateGrade(data.rating),
  }))
  .handler(async ({ data }) => {
    const { db, userId } = await serverDeps();
    const now = new Date();

    const existingRow = await db
      .selectFrom('card')
      .selectAll()
      .where('user_id', '=', userId)
      .where('page_number', '=', data.pageNumber)
      .where('mushaf', '=', CURRENT_MUSHAF)
      .executeTakeFirst();

    const fsrsCard = existingRow
      ? toFsrsCard(existingRow)
      : createEmptyCard(now);

    const { card: updatedCard } = scheduleCard(fsrsCard, data.rating, now);

    const row = toCardRow(
      updatedCard,
      userId,
      data.pageNumber,
      CURRENT_MUSHAF,
      existingRow?.id,
    );

    if (existingRow) {
      await db
        .updateTable('card')
        .set({
          due: row.due,
          stability: row.stability,
          difficulty: row.difficulty,
          elapsed_days: row.elapsed_days,
          scheduled_days: row.scheduled_days,
          reps: row.reps,
          lapses: row.lapses,
          state: row.state,
          last_review: row.last_review,
          updated_at: now.toISOString(),
        })
        .where('id', '=', existingRow.id)
        .execute();
    } else {
      await db
        .insertInto('card')
        .values({
          ...row,
          created_at: now.toISOString(),
          updated_at: now.toISOString(),
        })
        .execute();
    }

    return {
      id: row.id,
      pageNumber: data.pageNumber,
      card: updatedCard,
    };
  });

export const getDueCards = createServerFn({ method: 'GET' }).handler(
  async () => {
    const { db, userId } = await serverDeps();

    const rows = await db
      .selectFrom('card')
      .select(['page_number', 'due', 'state'])
      .where('user_id', '=', userId)
      .where('mushaf', '=', CURRENT_MUSHAF)
      .where('due', '<=', new Date().toISOString())
      .orderBy('due', 'asc')
      .execute();

    return rows.map((row) => ({
      pageNumber: row.page_number,
      due: row.due,
      state: row.state,
    }));
  },
);

export const getScheduledCards = createServerFn({ method: 'GET' }).handler(
  async () => {
    const { db, userId } = await serverDeps();

    const rows = await db
      .selectFrom('card')
      .select(['page_number', 'due', 'state'])
      .where('user_id', '=', userId)
      .where('mushaf', '=', CURRENT_MUSHAF)
      .orderBy('due', 'asc')
      .execute();

    return rows.map((row) => ({
      pageNumber: row.page_number,
      due: row.due,
      state: row.state,
    }));
  },
);
